import {
  InputGroup,
  ListboxGroup,
  NumericInputGroup,
  CheckboxGroup,
  PageSection,
  GenericCombobox
} from '@/common/components'
import useI18n from '@/shared/hooks/usei18n'
import { useCallback, useMemo } from 'react'
import { GoodsFamily, NON_EU_COUNTRIES } from '@/common'
import { orderBy } from 'es-toolkit'
import UnlocodeInput from '@/features/suppliers/components/UnlocodeInput'
import { InstallationFormData } from './InstallationForm.schema'
import { Partnership } from '@/features/suppliers/hooks/partnerships'

type InstallationFormProps = {
  data: InstallationFormData
  onChange: (data: InstallationFormData) => void
  isEditMode?: boolean
  partnership?: Partnership
}

export default function InstallationForm({
  data,
  onChange,
  isEditMode = false,
  partnership
}: InstallationFormProps) {
  const { t } = useI18n()

  const goodsFamilyOptions: {
    value: GoodsFamily
    label: string
  }[] = useMemo(() => {
    return Object.values(GoodsFamily).map(goodsFamily => ({
      value: goodsFamily,
      label: t(`common:goodsFamily.${goodsFamily}`)
    }))
  }, [t])

  const countryOptions = useMemo(() => {
    return orderBy(
      NON_EU_COUNTRIES.map(country => ({
        value: country.value,
        label: t(`common:countriesByIsoAlpha2:${country.value}`)
      })),
      [option => option.label.toLowerCase().normalize('NFKD')],
      ['asc']
    )
  }, [t])

  const {
    name,
    goodsFamily,
    usePartnershipAddress,
    street,
    streetNumber,
    street2,
    postCode,
    city,
    poBox,
    country,
    unlocode,
    longitude,
    latitude
  } = data

  const handleCheckboxChange = useCallback(
    (checked: boolean) => {
      if (checked && partnership) {
        onChange({
          ...data,
          usePartnershipAddress: true,
          street: partnership.operatorStreet || '',
          streetNumber: partnership.operatorStreetNumber || '',
          street2: partnership.operatorStreet2 || '',
          postCode: partnership.operatorPostCode || '',
          city: partnership.operatorCity || '',
          poBox: partnership.operatorPoBox || ''
        })

        return
      }

      onChange({
        ...data,
        usePartnershipAddress: false,
        street: '',
        streetNumber: '',
        street2: '',
        postCode: '',
        city: '',
        poBox: ''
      })
    },
    [partnership, onChange, data]
  )

  /* ────────────────── Validation helpers ────────────────── */
  const longitudeError = useMemo(() => {
    if (longitude === null || longitude === undefined || String(longitude).trim() === '') {
      return undefined
    }
    const numericValue = Number(longitude)
    return numericValue < -180 || numericValue > 180
      ? t('installations:installationInputFields.longitude.errors.outOfRange', {
          min: -180,
          max: 180
        })
      : undefined
  }, [longitude, t])

  const latitudeError = useMemo(() => {
    if (latitude === null || latitude === undefined || String(latitude).trim() === '') {
      return undefined
    }
    const numericValue = Number(latitude)
    return numericValue < -90 || numericValue > 90
      ? t('installations:installationInputFields.latitude.errors.outOfRange', {
          min: -90,
          max: 90
        })
      : undefined
  }, [latitude, t])

  console.log('country', country)
  console.log('countryOptions', countryOptions)

  return (
    <div className="space-y-6 pb-2">
      <PageSection title={t('installations:installationInputFields.sections.installationDetails')}>
        <div className="flex gap-4">
          <div className="flex-1">
            <InputGroup
              label={t('installations:installationInputFields.name.label')}
              required
              value={name}
              onChange={evt => onChange({ ...data, name: evt.target.value })}
            />
          </div>
          <div className="flex-1">
            <ListboxGroup<GoodsFamily>
              options={goodsFamilyOptions}
              required
              tooltip={t('installations:installationInputFields.economicActivity.tooltip')}
              label={t('installations:installationInputFields.economicActivity.label')}
              value={goodsFamily}
              onChange={value => onChange({ ...data, goodsFamily: value })}
              disabled={isEditMode}
            />
          </div>
        </div>
      </PageSection>
      <PageSection title={t('installations:installationInputFields.sections.address')}>
        {usePartnershipAddress !== undefined && !isEditMode && partnership && (
          <div className="mb-4">
            <CheckboxGroup
              id="usePartnershipAddress"
              checked={usePartnershipAddress}
              onChange={handleCheckboxChange}
              label={t('installations:installationInputFields.useSupplierAddress')}
            />
          </div>
        )}
        <div className="grid grid-cols-2 gap-4">
          <InputGroup
            id="street"
            label={t('common:inputFields.street.label')}
            value={street}
            onChange={evt => onChange({ ...data, street: evt.target.value })}
          />

          <InputGroup
            id="streetNumber"
            label={t('common:inputFields.streetNumber.label')}
            value={streetNumber}
            onChange={evt => onChange({ ...data, streetNumber: evt.target.value })}
          />
        </div>

        <InputGroup
          id="street2"
          label={t('common:inputFields.street2.label')}
          value={street2}
          onChange={evt => onChange({ ...data, street2: evt.target.value })}
        />

        <div className="grid md:grid-cols-3 gap-4">
          <InputGroup
            id="postCode"
            label={t('common:inputFields.postCode.label')}
            value={postCode}
            onChange={evt => onChange({ ...data, postCode: evt.target.value })}
            required={
              NON_EU_COUNTRIES.find(countryItem => countryItem.value === country)
                ?.isPostCodeRequired
            }
          />
          <InputGroup
            id="city"
            label={t('common:inputFields.city.label')}
            value={city}
            onChange={evt => onChange({ ...data, city: evt.target.value })}
            required
          />
          <InputGroup
            id="poBox"
            label={t('common:inputFields.poBox.label')}
            value={poBox}
            onChange={evt => onChange({ ...data, poBox: evt.target.value })}
          />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <GenericCombobox
            required
            label={t('common:inputFields.country.label')}
            items={countryOptions}
            value={
              country
                ? { value: country, label: t(`common:countriesByIsoAlpha2:${country}`) }
                : null
            }
            onChange={evt => onChange({ ...data, country: evt?.value || '' })}
            disabled={isEditMode}
          />
          <UnlocodeInput
            countryCodeFilter={country}
            label={t('installations:installationInputFields.unlocode.label')}
            value={unlocode}
            tooltip={t('installations:installationInputFields.unlocode.tooltip')}
            tooltipPlacement="top"
            onChange={value => onChange({ ...data, unlocode: value || '' })}
          />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <NumericInputGroup
            id="longitude"
            label={t('installations:installationInputFields.longitude.label')}
            value={longitude ? longitude.toString() : ''}
            onChange={evt => onChange({ ...data, longitude: Number(evt.target.value) })}
            tooltip={t('installations:installationInputFields.longitude.tooltip')}
            tooltipPlacement="top"
            error={longitudeError}
            numericFormat={{
              decimalScale: 6,
              min: -180,
              max: 180,
              fixedDecimalScale: true
            }}
          />
          <NumericInputGroup
            id="latitude"
            label={t('installations:installationInputFields.latitude.label')}
            value={latitude ? latitude.toString() : ''}
            onChange={evt => onChange({ ...data, latitude: Number(evt.target.value) })}
            tooltip={t('installations:installationInputFields.latitude.tooltip')}
            tooltipPlacement="top"
            error={latitudeError}
            numericFormat={{
              decimalScale: 6,
              min: -90,
              max: 90,
              fixedDecimalScale: true
            }}
          />
        </div>
      </PageSection>
    </div>
  )
}
